import { create } from 'zustand';

/**
 * Undo Store
 *
 * Manages undo/redo history for reversible actions.
 * Stores action snapshots that can be replayed backwards.
 */

export type UndoableAction = {
  type: 'token:add' | 'token:remove' | 'token:move' | 'mob:add' | 'mob:remove';
  description: string;
  undo: () => void;
  redo: () => void;
};

interface UndoState {
  // History stacks
  undoStack: UndoableAction[];
  redoStack: UndoableAction[];

  // Max history size
  maxHistory: number;

  // Actions
  push: (action: UndoableAction) => void;
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
  clear: () => void;
}

const MAX_HISTORY = 50;

export const useUndoStore = create<UndoState>((set, get) => ({
  undoStack: [],
  redoStack: [],
  maxHistory: MAX_HISTORY,

  push: (action) => {
    set((state) => {
      const newStack = [...state.undoStack, action];
      // Trim to max size
      if (newStack.length > state.maxHistory) {
        newStack.shift();
      }
      return {
        undoStack: newStack,
        redoStack: [], // Clear redo stack on new action
      };
    });
  },

  undo: () => {
    const { undoStack, redoStack } = get();
    if (undoStack.length === 0) return;

    const action = undoStack[undoStack.length - 1];
    action.undo();

    set({
      undoStack: undoStack.slice(0, -1),
      redoStack: [...redoStack, action],
    });
  },

  redo: () => {
    const { undoStack, redoStack } = get();
    if (redoStack.length === 0) return;

    const action = redoStack[redoStack.length - 1];
    action.redo();

    set({
      undoStack: [...undoStack, action],
      redoStack: redoStack.slice(0, -1),
    });
  },

  canUndo: () => get().undoStack.length > 0,
  canRedo: () => get().redoStack.length > 0,

  clear: () => set({ undoStack: [], redoStack: [] }),
}));
